// ============================================================================
// Foreign recipient address support for the IRS A2A flow.
//
// The IRS AddressGrpType is a CHOICE of USAddress | ForeignAddress. For a recipient
// with a FOREIGN_COUNTRY, the generators still emit a USAddress (with the foreign
// province in StateAbbreviationCd, which is invalid). This module rewrites those into
// a valid ForeignAddress, centrally, for every form.
//
// ForeignAddressType element order (per IRS-IRefileTypes.xsd):
//   AddressLine1Txt, AddressLine2Txt?, CityNm?, ProvinceOrStateNm?, CountryCd, ForeignPostalCd?
//
// CountryCd is the IRS CountryType enumeration. NOTE: these are the IRS's own legacy
// 2-letter codes, which are NOT all ISO 3166-1 (e.g. Bahamas=BF, Bahrain=BA, Spain=SP,
// Cambodia=CB, Algeria=AG). So the country NAME -> code map is built straight from the
// schema's enumeration documentation — authoritative and always in sync.
// ============================================================================

import * as fs from "fs";
import { schemaPath } from "../paths";
import { irsCountryCode } from "../validate/countryCodes.generated";
import { rewriteForeignAddresses } from "./foreignAddressRewrite";

// EXTRACTION NOTE: the country table used to be read from a working-directory path
// ("src/functions/.../schemas/COMMON/IRS-IRefileTypes.xsd"). It now resolves inside the
// schema package shipped with this module, so it works from src/ and from dist/ alike.
const COMMON_SCHEMA_PARTS = ["TY2025", "COMMON", "IRS-IRefileTypes.xsd"];

// Built once from the schema: UPPERCASE country name -> IRS code, plus code -> code.
let countryMap: Record<string, string> | null = null;
function getCountryMap(schemaRoot?: string): Record<string, string> {
  if (countryMap) return countryMap;
  const map: Record<string, string> = {};
  try {
    const xsd = fs.readFileSync(schemaPath(schemaRoot, ...COMMON_SCHEMA_PARTS), "utf8");
    const block =
      xsd.match(/<xsd:simpleType name="CountryType">[\s\S]*?<\/xsd:simpleType>/)?.[0] ||
      "";
    const re =
      /<xsd:enumeration value="([A-Za-z]{2})">[\s\S]*?<xsd:documentation>([^<]+)<\/xsd:documentation>/g;
    // The part before a comma ("MAN, ISLE OF" -> "MAN"), only when it names one country.
    // "KOREA" is both KS and KN; taking the first seen filed South Korea as North Korea.
    const prefixes: Record<string, Set<string>> = {};
    let m: RegExpExecArray | null;
    while ((m = re.exec(block))) {
      const code = m[1].toUpperCase();
      const name = m[2].trim().toUpperCase();
      map[name] = code;
      map[code] = code; // a valid code maps to itself
      const comma = name.indexOf(",");
      const prefix = comma > 0 ? name.slice(0, comma).trim() : "";
      if (prefix && !prefix.includes("(")) (prefixes[prefix] ??= new Set()).add(code);
    }
    for (const [prefix, codes] of Object.entries(prefixes)) {
      if (codes.size === 1 && !map[prefix]) map[prefix] = [...codes][0];
    }
  } catch {
    /* leave empty — countryToIso returns null, record fails XSD with a clear country error */
  }
  // A few common aliases the schema spells differently.
  for (const [a, c] of [["UK", "UK"], ["USA", "US"], ["UAE", "TC"]] as const) {
    if (map[c] && !map[a]) map[a] = map[c];
  }
  countryMap = map;
  return map;
}

/**
 * Map a source FOREIGN_COUNTRY value (name or code) to the IRS CountryType code, or null.
 *
 * The schema's own names come first. Anything else falls back to irsCountryCode, the table the
 * record checks use, so a country the checks accept ("UNITED KINGDOM", "GREAT BRITAIN") is
 * never one the XML then writes as a code the IRS refuses.
 */
export function countryToIso(raw: any, schemaRoot?: string): string | null {
  const v = String(raw ?? "").trim().toUpperCase();
  if (!v) return null;
  return getCountryMap(schemaRoot)[v] ?? irsCountryCode(v);
}

/**
 * Rewrite the recipient USAddress into a ForeignAddress for every recipient whose record
 * carries a FOREIGN_COUNTRY (see foreignAddressRewrite.ts, which the browser converter shares),
 * resolving the country through the schema's own names.
 */
export function applyForeignAddresses(
  xml: string,
  records: any[],
  schemaRoot?: string
): string {
  return rewriteForeignAddresses(xml, records, (country) => countryToIso(country, schemaRoot));
}
